import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from 'react-router-dom';
import api from "../services/api";
import { formatToDisplayDate, formatToISODate, parseDisplayDate } from "../services/dateFormatter";
import Modal from "../components/Modal";
import InvoiceForm from "./InvoiceForm";
import DateInput from "../components/DateInput";
import Pagination from "../components/Pagination";

const EMPTY = {
  amc: "", billing_split: "Full",
  invoice_number: "", invoice_date: "",
  period_start: "", period_end: "",
  actual_amount: "", tax_percentage: "18", status: "Unpaid",
  period_index: 0,
};
const RECEIPT_EMPTY = {
  receipt_date: "", amount_received: "", tds_it: "", tds_gst: "",
};

// ── Billing split options ─────────────────────────────────────────────────
const SPLIT_OPTIONS = ["Full","Half-Yearly","Quarterly","Monthly"];

function computePeriodLabel(contractStart, periodStart, installment) {
  if (!periodStart) return null;
  try {
    const start = new Date(periodStart);
    const contract = new Date(contractStart || periodStart);
    if (isNaN(start) || isNaN(contract)) return null;
    const months = (start.getFullYear() - contract.getFullYear()) * 12 + (start.getMonth() - contract.getMonth());
    if (installment === "Monthly") {
      return start.toLocaleString('default', { month: 'short', year: 'numeric' });
    }
    if (installment === "Quarterly") {
      return `Q${Math.floor(months / 3) + 1}`;
    }
    if (installment === "Half-Yearly") {
      const index = Math.floor(months / 6) + 1;
      const suffix = index === 1 ? 'st' : index === 2 ? 'nd' : index === 3 ? 'rd' : 'th';
      return `${index}${suffix} Half`;
    }
    return "Full";
  } catch (e) {
    return null;
  }
}

function computePeriodAndAmount(amc, split) {
  if (!amc) return { period_start: "", period_end: "", amount: "" };
  const start = new Date(amc.start_date);
  let end = new Date(start);
  let divisor = 1;
  if      (split === "Monthly")     { end.setMonth(end.getMonth() + 1);       divisor = 12; }
  else if (split === "Quarterly")   { end.setMonth(end.getMonth() + 3);       divisor = 4;  }
  else if (split === "Half-Yearly") { end.setMonth(end.getMonth() + 6);       divisor = 2;  }
  else                              { end.setFullYear(end.getFullYear() + 1);  divisor = 1;  }
  end.setDate(end.getDate() - 1);
  const base = Math.round(parseFloat(amc.value) / divisor);
  return {
    period_start: amc.start_date,
    period_end:   end.toISOString().split("T")[0],
    amount:       String(base),
    label:        computePeriodLabel(amc.start_date, amc.start_date, split),
  };
}

const fmt = n => "₹" + Math.round(parseFloat(n) || 0).toLocaleString("en-IN");

export default function Invoices({ role }) {
  const [invoices,  setInvoices]  = useState([]);
  const [contracts, setContracts] = useState([]);
  const [periodOptions, setPeriodOptions] = useState([]);
  const [modal,     setModal]     = useState(null);
  const [form,      setForm]      = useState(EMPTY);
  const [editId,    setEditId]    = useState(null);
  const [viewData,  setViewData]  = useState(null);
  const [receipt,   setReceipt]   = useState(RECEIPT_EMPTY);
  const [receiptInv,setReceiptInv]= useState(null);
  const [similarInvoices, setSimilarInvoices] = useState([]);
  const [showSimilarWarning, setShowSimilarWarning] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);
  const [page, setPage] = useState(1);
  const pageSize = 12;
  const [totalInvoices, setTotalInvoices] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState(() => searchParams.get('q') || '');
  const [invoiceNumberFilter, setInvoiceNumberFilter] = useState(() => searchParams.get('invoice_number') || '');
  const [customerFilter, setCustomerFilter] = useState(() => searchParams.get('customer') || '');
  const [amcFilter, setAmcFilter] = useState(() => searchParams.get('amc') || '');
  const [invoiceDateFrom, setInvoiceDateFrom] = useState(() => searchParams.get('invoice_date_after') || '');
  const [invoiceDateTo, setInvoiceDateTo] = useState(() => searchParams.get('invoice_date_before') || '');
  const [periodStartFrom, setPeriodStartFrom] = useState(() => searchParams.get('period_start_after') || '');
  const [periodStartTo, setPeriodStartTo] = useState(() => searchParams.get('period_start_before') || '');
  const [minGrandTotal, setMinGrandTotal] = useState(() => searchParams.get('min_grand_total') || '');
  const [maxGrandTotal, setMaxGrandTotal] = useState(() => searchParams.get('max_grand_total') || '');
  const [statusFilter, setStatusFilter] = useState(() => searchParams.get('status') || '');
  const [ordering, setOrdering] = useState(() => searchParams.get('ordering') || '');

  const toggleOrdering = field => {
    setOrdering(curr => {
      if (curr === field) return `-${field}`;
      if (curr === `-${field}`) return '';
      return field;
    });
    setPage(1);
  };

  const sortIndicator = field => ordering === field ? '↑' : ordering === `-${field}` ? '↓' : '↕';
  const renderSortHeading = (label, field) => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '.35rem' }}>
      <span>{label}</span>
      <button type="button" onClick={() => toggleOrdering(field)} style={{ border: 'none', background: 'transparent', color: '#94a3b8', cursor: 'pointer', fontSize: '.9rem' }}>
        {sortIndicator(field)}
      </button>
    </div>
  );

  const buildQuery = (p = page) => {
    const parts = [`page=${p}`, `page_size=${pageSize}`];
    if (searchTerm) parts.push(`search=${encodeURIComponent(searchTerm)}`);
    if (invoiceNumberFilter) parts.push(`invoice_number=${encodeURIComponent(invoiceNumberFilter)}`);
    if (customerFilter) parts.push(`customer=${encodeURIComponent(customerFilter)}`);
    if (amcFilter) {
      const clean = amcFilter.replace(/^AMC-?/i, '').trim();
      if (clean) parts.push(`amc=${encodeURIComponent(clean)}`);
    }
    if (invoiceDateFrom) {
      const isoDate = parseDisplayDate(invoiceDateFrom);
      if (isoDate) parts.push(`invoice_date_after=${encodeURIComponent(isoDate)}`);
    }
    if (invoiceDateTo) {
      const isoDate = parseDisplayDate(invoiceDateTo);
      if (isoDate) parts.push(`invoice_date_before=${encodeURIComponent(isoDate)}`);
    }
    if (periodStartFrom) {
      const isoDate = parseDisplayDate(periodStartFrom);
      if (isoDate) parts.push(`period_start_after=${encodeURIComponent(isoDate)}`);
    }
    if (periodStartTo) {
      const isoDate = parseDisplayDate(periodStartTo);
      if (isoDate) parts.push(`period_start_before=${encodeURIComponent(isoDate)}`);
    }
    if (minGrandTotal) parts.push(`min_grand_total=${encodeURIComponent(minGrandTotal)}`);
    if (maxGrandTotal) parts.push(`max_grand_total=${encodeURIComponent(maxGrandTotal)}`);
    if (statusFilter) parts.push(`status=${encodeURIComponent(statusFilter)}`);
    if (ordering) parts.push(`ordering=${encodeURIComponent(ordering)}`);
    return parts.join('&');
  };

  const load = (p = 1) => {
    const q = buildQuery(p);
    api.get(`invoices/?${q}`).then(r => {
      const data = r.data || {};
      setInvoices(data.results || []);
      setTotalInvoices(data.count || (data.results ? data.results.length : 0));
    }).catch(() => {});
    api.get(`amc/?page_size=2000`).then(r => setContracts(r.data.results || r.data)).catch(() => {});
    // reflect state in URL (preserve across navigation)
    const params = {};
    if (searchTerm) params.q = searchTerm;
    if (invoiceNumberFilter) params.invoice_number = invoiceNumberFilter;
    if (customerFilter) params.customer = customerFilter;
    if (amcFilter) params.amc = amcFilter;
    if (invoiceDateFrom) params.invoice_date_after = invoiceDateFrom;
    if (invoiceDateTo) params.invoice_date_before = invoiceDateTo;
    if (periodStartFrom) params.period_start_after = periodStartFrom;
    if (periodStartTo) params.period_start_before = periodStartTo;
    if (minGrandTotal) params.min_grand_total = minGrandTotal;
    if (maxGrandTotal) params.max_grand_total = maxGrandTotal;
    if (statusFilter) params.status = statusFilter;
    if (ordering) params.ordering = ordering;
    if (p && p > 1) params.page = String(p);
    setSearchParams(params, { replace: true });
  };

  useEffect(() => { // initialize from URL params
    const q = searchParams.get('q') || '';
    const n = searchParams.get('invoice_number') || '';
    const c = searchParams.get('customer') || '';
    const a = searchParams.get('amc') || '';
    const idf = searchParams.get('invoice_date_after') || '';
    const idt = searchParams.get('invoice_date_before') || '';
    const psf = searchParams.get('period_start_after') || '';
    const pst = searchParams.get('period_start_before') || '';
    const minGT = searchParams.get('min_grand_total') || '';
    const maxGT = searchParams.get('max_grand_total') || '';
    const s = searchParams.get('status') || '';
    const o = searchParams.get('ordering') || '';
    const p = parseInt(searchParams.get('page') || '1', 10) || 1;
    setSearchTerm(q);
    setInvoiceNumberFilter(n);
    setCustomerFilter(c);
    setAmcFilter(a);
    setInvoiceDateFrom(idf);
    setInvoiceDateTo(idt);
    setPeriodStartFrom(psf);
    setPeriodStartTo(pst);
    setMinGrandTotal(minGT);
    setMaxGrandTotal(maxGT);
    setStatusFilter(s);
    setOrdering(o);
    setPage(p);
  }, []);

  useEffect(() => { load(page); }, [page, searchTerm, invoiceNumberFilter, customerFilter, amcFilter, invoiceDateFrom, invoiceDateTo, periodStartFrom, periodStartTo, minGrandTotal, maxGrandTotal, statusFilter, ordering, showDeleted]);

  // If redirected from dashboard with a preferred AMC/period, open create modal
  // Run this after `contracts` are loaded to avoid a race (contracts are needed
  // to compute periods and pre-select the correct slot).
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('ihrd_pref_invoice');
      if (!raw) return;
      const obj = JSON.parse(raw);
      // Only proceed if we have contracts loaded
      if (obj && obj.amc && obj.period_start && contracts && contracts.length > 0) {
        openCreateFor(obj.amc, obj.period_start);
        sessionStorage.removeItem('ihrd_pref_invoice');
      }
    } catch (err) {
      // keep silent but keep a console log for easier local debugging
      try { console.error('pref invoice open error', err); } catch(_) {}
    }
  }, [contracts]);

  // When AMC or split changes — recalculate period + amount
  const recalculate = useCallback((amcId, split, cur) => {
    const amc = contracts.find(c => String(c.id) === String(amcId));
    if (!amc) return { ...cur, amc: amcId, billing_split: split };
    // compute all periods for this contract + split
    const periods = computeAllPeriods(amc, split);
    setPeriodOptions(periods);
    // pick index from cur.period_index or first missing
    let idx = cur.period_index || 0;
    const existingStarts = invoices.filter(i => String(i.amc) === String(amcId)).map(i => i.period_start);
    const missingIdx = periods.findIndex(p => !existingStarts.includes(p.period_start));
    if (missingIdx >= 0) idx = missingIdx;
    const chosen = periods[idx] || periods[0] || { period_start: '', period_end: '', amount: '', label: '' };
    return { ...cur, amc: amcId, billing_split: split, period_start: chosen.period_start, period_end: chosen.period_end, actual_amount: chosen.amount, period_index: idx, period_label: chosen.label };
  }, [contracts, invoices]);

  const handleAmcChange = e => {
    setForm(f => {
      const amc = contracts.find(c => String(c.id) === String(e.target.value));
      const split = amc ? amc.installment || f.billing_split : f.billing_split;
      return recalculate(e.target.value, split, { ...f, billing_split: split });
    });
  };
  const handleSplitChange = e => setForm(f => recalculate(f.amc, e.target.value, f));
  const handlePeriodIndexChange = e => {
    const idx = parseInt(e.target.value, 10);
    const p = periodOptions[idx];
    if (!p) return;
    setForm(f => ({ ...f, period_index: idx, period_start: p.period_start, period_end: p.period_end, actual_amount: p.amount }));
  };

  // Open the create modal; optional parameters allow preselecting an AMC or period
  const openCreate = () => openCreateFor();
  const openCreateFor = (amcId=null, prefPeriodStart=null) => {
    const base = { ...EMPTY };
    if (amcId) base.amc = amcId;
    setForm(base);
    setEditId(null);
    setModal("form");
    // If an AMC id is provided, compute periods immediately and preselect
    if (amcId) {
      const amc = contracts.find(c => String(c.id) === String(amcId));
      if (amc) {
        const split = amc.installment || base.billing_split;
        const periods = computeAllPeriods(amc, split);
        const existingStarts = invoices.filter(i => String(i.amc) === String(amcId)).map(i => i.period_start);
        const pending = periods.filter(period => !existingStarts.includes(period.period_start));
        setPeriodOptions(pending);
        let idx = 0;
        if (prefPeriodStart) {
          const found = pending.findIndex(p => p.period_start === prefPeriodStart);
          if (found >= 0) idx = found;
        }
        const chosen = pending[idx] || { period_start: '', period_end: '', amount: '', label: '' };
        const customerValue = String(amc.customer ?? amc.customer_id ?? amc.customer_name ?? '');
        setForm(f => ({ ...f, customer: customerValue, amc: amcId, billing_split: split, period_index: idx, period_start: chosen.period_start, period_end: chosen.period_end, actual_amount: chosen.amount, period_label: chosen.label, description: amc.description || '' }));
      }
    }
  };
  const openEdit   = inv => {
    setForm({
      id: inv.id,
      amc: inv.amc, billing_split: "Full",
      invoice_number: inv.invoice_number || "", invoice_date: inv.invoice_date || "",
      period_start: inv.period_start, period_end: inv.period_end,
      actual_amount: inv.actual_amount, tax_percentage: inv.tax_percentage,
      status: inv.status,
      // preserve existing description (fall back to AMC description if present)
      description: inv.description || inv.amc_description || '',
      invoice_locked: Boolean(inv.invoice_number || inv.invoice_date),
    });
    setEditId(inv.id); setModal("form");
  };
  const openView    = inv => { setViewData(inv); setModal("view"); };
  const openReceipt = inv => {
    setReceiptInv(inv);
    setReceipt({
      receipt_date:    inv.receipt_date    || "",
      amount_received: inv.amount_received || "",
      tds_it:          inv.tds_it          || "",
      tds_gst:         inv.tds_gst         || "",
    });
    setModal("receipt");
  };
  const close = () => { setModal(null); setViewData(null); setReceiptInv(null); };

  const errMsg = (err, label) => {
    const data = err?.response?.data;
    if (!data) { alert("Cannot reach backend — ensure Django is running on port 8080."); return; }
    if (typeof data === "string" && data.includes("<!DOCTYPE")) {
      alert("Database error. Run: python manage.py migrate"); return;
    }
    alert(`${label}:\n\n` + JSON.stringify(data, null, 2));
  };

  const save = async e => {
    // kept for compatibility with legacy form submissions (not used by modal form)
    if (e && e.preventDefault) e.preventDefault();
    const payload = { ...form };
    delete payload.billing_split; delete payload.period_index;
    try {
      if (editId) await api.put(`invoices/${editId}/`, payload);
      else await api.post("invoices/", payload);
      close(); load();
    } catch (err) { errMsg(err, "Save failed"); }
  };

  const saveReceipt = async e => {
    e.preventDefault();
    const payload = {};
    if (receipt.receipt_date) {
      const receiptDateIso = parseDisplayDate(receipt.receipt_date);
      if (!receiptDateIso) {
        alert('Please enter Receipt Date in DD/MM/YYYY format.');
        return;
      }
      payload.receipt_date = receiptDateIso;
    }
    if (receipt.amount_received) payload.amount_received = receipt.amount_received;
    if (receipt.tds_it)          payload.tds_it          = receipt.tds_it;
    if (receipt.tds_gst)         payload.tds_gst         = receipt.tds_gst;
    try {
      await api.patch(`invoices/${receiptInv.id}/`, payload);
      close(); load();
    } catch (err) { errMsg(err, "Receipt save failed"); }
  };

  const isAdmin = role === 'Admin';

  const del = async id => {
    if (!window.confirm("Delete this invoice?")) return;
    try { await api.delete(`invoices/${id}/`); load(); }
    catch { alert("Delete failed."); }
  };

  const restore = async id => {
    if (!window.confirm("Restore this invoice?")) return;
    try {
      await api.post(`invoices/${id}/restore/`);
      alert("Invoice restored successfully");
      load(page);
    } catch (err) {
      alert("Restore failed: " + (err?.response?.data?.detail || err.message));
    }
  };

  // Live tax calculation for form
  const base  = parseFloat(form.actual_amount)  || 0;
  const pct   = parseFloat(form.tax_percentage) || 0;
  const tax   = base * (pct / 100);
  const grand = base + tax;

  // Live receipt calculation
  const rAmt  = parseFloat(receipt.amount_received) || 0;
  const rTdsIT = parseFloat(receipt.tds_it)  || 0;
  const rTdsGST = parseFloat(receipt.tds_gst) || 0;
  const rNet     = rAmt + rTdsIT + rTdsGST;
  const invGrand = receiptInv ? parseFloat(receiptInv.grand_total) : 0;
  const rawPending = invGrand - rNet;
  // Treat sub-₹1 balance as zero (matches backend logic)
  const rPending = rawPending >= 1.0 ? rawPending : 0;

  const statusBadge = s =>
    s === "Paid" ? "badge-emerald" : s === "Partial" ? "badge-purple" : "badge-red";

  const amcLabel = id => {
    const c = contracts.find(x => String(x.id) === String(id));
    return c ? `AMC-${c.id} — ${c.customer_name || ""}` : `AMC-${id}`;
  };

  // Compute all billing periods for a contract (mirrors backend `_all_billing_periods`)
  function computeAllPeriods(amc, split) {
    if (!amc) return [];
    const periods = [];
    const start = new Date(amc.start_date);
    const end = new Date(amc.end_date);
    if (split === 'Full') {
      periods.push({
        period_start: amc.start_date,
        period_end: amc.end_date,
        amount: String(Math.round(parseFloat(amc.value) || 0)),
        label: computePeriodLabel(amc.start_date, amc.start_date, split),
      });
      return periods;
    }
    let step = new Date(start);
    while (step <= end) {
      const p_start = new Date(step);
      let nxt = new Date(step);
      if (split === 'Monthly') nxt.setMonth(nxt.getMonth() + 1);
      else if (split === 'Quarterly') nxt.setMonth(nxt.getMonth() + 3);
      else if (split === 'Half-Yearly') nxt.setMonth(nxt.getMonth() + 6);
      else break;
      const p_end_d = new Date(nxt);
      p_end_d.setDate(p_end_d.getDate() - 1);
      const p_end = p_end_d > end ? end : p_end_d;
      const divisor = split === 'Monthly' ? 12 : split === 'Quarterly' ? 4 : split === 'Half-Yearly' ? 2 : 1;
      const amount = String(Math.round(parseFloat(amc.value || 0) / divisor));
      const periodStartString = p_start.toISOString().split('T')[0];
      periods.push({
        period_start: periodStartString,
        period_end: p_end.toISOString().split('T')[0],
        amount,
        label: computePeriodLabel(amc.start_date, periodStartString, split),
      });
      step = nxt;
      if (step > end) break;
    }
    return periods;
  }

  // ── Label helpers ─────────────────────────────────────────────────────

  // Check for similar invoices before saving
  const checkAndWarnSimilar = useCallback(async () => {
    if (!form.customer || !form.actual_amount || !form.period_start) return true;
    try {
      const res = await api.post('invoices/check-similar/', {
        customer_id: form.customer,
        amount: form.actual_amount,
        period_start: form.period_start,
      });
      const similar = res.data.similar_invoices || [];
      if (similar.length > 0) {
        setSimilarInvoices(similar);
        setShowSimilarWarning(true);
        return false; // Block submission until user confirms
      }
    } catch (_) {}
    return true; // Allow if API error
  }, [form.customer, form.actual_amount, form.period_start]);

  return (
    <div>
      {/* ── Header ── */}
      <div className="section-header">
        <div>
          <h2>Invoices &amp; Billing</h2>
          <p>View, build, and audit transactional ledgers with line-item tax details.</p>
        </div>
        <div style={{ display: 'flex', gap: '.5rem', alignItems: 'center' }}>
          <button className="btn btn-emerald" onClick={openCreate}>
          <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4"/>
          </svg>
          Generate Invoice
        </button>
          <button className="btn btn-slate btn-sm" onClick={() => load(page)}>Refresh</button>
          <button className="btn btn-slate btn-sm" onClick={() => {
            setSearchTerm('');
            setInvoiceNumberFilter('');
            setCustomerFilter('');
            setAmcFilter('');
            setInvoiceDateFrom('');
            setInvoiceDateTo('');
            setPeriodStartFrom('');
            setPeriodStartTo('');
            setMinGrandTotal('');
            setMaxGrandTotal('');
            setStatusFilter('');
            setOrdering('');
            setPage(1);
          }}>Clear Filters</button>
          {isAdmin && <label style={{ display: 'flex', alignItems: 'center', gap: '.5rem', cursor: 'pointer', padding: '.5rem .75rem', background: '#334155', borderRadius: '.25rem', fontSize: '.875rem' }}>
            <input type="checkbox" checked={showDeleted} onChange={e => { setShowDeleted(e.target.checked); setPage(1); }} style={{ cursor: 'pointer' }} />
            Show Deleted
          </label>}
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '.5rem', marginBottom: '.75rem', alignItems: 'center' }}>
        <input placeholder="Search invoice no or client" value={searchTerm} onChange={e => { setSearchTerm(e.target.value); setPage(1); }} style={{ padding: '.5rem', minWidth: 220 }} />
        <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}>
          <option value="">All Status</option>
          <option>Unpaid</option><option>Partial</option><option>Paid</option>
        </select>
        <select value={ordering} onChange={e => { setOrdering(e.target.value); setPage(1); }}>
          <option value="">Default order</option>
          <option value="-invoice_date">Newest first</option>
          <option value="invoice_date">Oldest first</option>
          <option value="-grand_total">High amount first</option>
          <option value="grand_total">Low amount first</option>
        </select>
      </div>
      <Pagination page={page} setPage={setPage} total={totalInvoices} pageSize={pageSize} />

      {/* ── Table ── */}
      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <th>{renderSortHeading('Invoice No', 'invoice_number')}</th>
              <th>{renderSortHeading('Date', 'invoice_date')}</th>
              <th>{renderSortHeading('Client', 'amc__customer__name')}</th>
              <th>{renderSortHeading('Period', 'period_start')}</th>
              <th>Description</th>
              <th>{renderSortHeading('Grand Total', 'grand_total')}</th>
              <th>Net Received</th>
              <th>Pending</th>
              <th>{renderSortHeading('Status', 'status')}</th>
              <th className="th-right">Actions</th>
            </tr>
            <tr>
              <th>
                <input type="text" value={invoiceNumberFilter} placeholder="Invoice #" onChange={e => { setInvoiceNumberFilter(e.target.value); setPage(1); }} />
              </th>
              <th>
                <div className="date-range-row">
                  <div className="date-range-part">
                    <DateInput value={invoiceDateFrom} onChange={v => { setInvoiceDateFrom(v); setPage(1); }} placeholder="From (DD/MM/YYYY)" />
                  </div>
                  <span className="date-range-separator">–</span>
                  <div className="date-range-part">
                    <DateInput value={invoiceDateTo} onChange={v => { setInvoiceDateTo(v); setPage(1); }} placeholder="To (DD/MM/YYYY)" />
                  </div>
                </div>
              </th>
              <th>
                <input type="text" value={customerFilter} placeholder="Customer" onChange={e => { setCustomerFilter(e.target.value); setPage(1); }} />
              </th>
              <th>
                <div className="date-range-row">
                  <div className="date-range-part">
                    <DateInput value={periodStartFrom} onChange={v => { setPeriodStartFrom(v); setPage(1); }} placeholder="From (DD/MM/YYYY)" />
                  </div>
                  <span className="date-range-separator">–</span>
                  <div className="date-range-part">
                    <DateInput value={periodStartTo} onChange={v => { setPeriodStartTo(v); setPage(1); }} placeholder="To (DD/MM/YYYY)" />
                  </div>
                </div>
              </th>
              <th></th>
              <th>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '.25rem' }}>
                  <input type="number" value={minGrandTotal} placeholder="Min" onChange={e => { setMinGrandTotal(e.target.value); setPage(1); }} style={{ width: '100%' }} />
                  <input type="number" value={maxGrandTotal} placeholder="Max" onChange={e => { setMaxGrandTotal(e.target.value); setPage(1); }} style={{ width: '100%' }} />
                </div>
              </th>
              <th>
                <input type="text" value={amcFilter} placeholder="AMC #" onChange={e => { setAmcFilter(e.target.value); setPage(1); }} />
              </th>
              <th>
                <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}>
                  <option value="">All</option>
                  <option>Unpaid</option><option>Partial</option><option>Paid</option>
                </select>
              </th>
              <th className="th-right"></th>
            </tr>
          </thead>
          <tbody>
            {invoices.length === 0 && (
              <tr><td colSpan="9">
                <div className="empty-state">No invoices generated yet.</div>
              </td></tr>
            )}
            {invoices.map(inv => (
              <tr key={inv.id}>
                <td style={{ fontWeight: 700, color: "#34d399" }}>
                  {inv.invoice_number || `INV-${inv.id}`}
                </td>
                <td className="date-cell" style={{ color: "#94a3b8", fontSize: ".8rem" }}>{formatToDisplayDate(inv.invoice_date) || "—"}</td>
                <td style={{ color: "#f1f5f9" }}>{inv.amc_customer_name || "—"}</td>
                <td className="date-range-cell" style={{ color: "#94a3b8", fontSize: ".78rem" }}>
                  <div className="date-range-line">
                    <span>{formatToDisplayDate(inv.period_start)}</span>
                    <span>→</span>
                    <span>{formatToDisplayDate(inv.period_end)}</span>
                  </div>
                  {inv.period_label ? (<div style={{ fontSize: '.72rem', color: '#94a3b8' }}>({inv.period_label})</div>) : null}
                </td>
                <td style={{ color: "#cbd5e1", fontSize: ".8rem", whiteSpace: "normal", wordWrap: "break-word", overflowWrap: "break-word" }}>
                  {(() => { const d = (inv.description || inv.amc_description || '').trim(); return d ? d.substring(0, 100) + (d.length > 100 ? "..." : "") : "—"; })()}
                </td>
                <td className="amount-cell" style={{ color: "#34d399", fontWeight: 600 }}>{fmt(inv.grand_total)}</td>
                <td className="amount-cell" style={{ color: "#22d3ee" }}>
                  {inv.net_received_total > 0 ? fmt(inv.net_received_total) : "—"}
                </td>
                <td className="amount-cell" style={{ color: parseFloat(inv.pending_amount) > 0 ? "#f87171" : "#34d399", fontWeight: 600 }}>
                  {fmt(inv.pending_amount)}
                </td>
                <td>
                  <span className={`badge ${statusBadge(inv.status)}`}>{inv.status}</span>
                </td>
                <td className="td-right">
                  <div className="actions-cell">
                    <button className="btn btn-slate btn-sm" onClick={() => openView(inv)}>View</button>
                    <button className="btn btn-emerald btn-sm" onClick={() => openEdit(inv)}>Edit</button>
                    {inv.status !== "Paid" && (
                      <button className="btn btn-cyan btn-sm" onClick={() => openReceipt(inv)}>
                        Receipt
                      </button>
                    )}
                    {isAdmin && (
                      <button className="btn btn-red btn-sm" onClick={() => del(inv.id)}>Del</button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ══ CREATE / EDIT MODAL — now handled by InvoiceForm (local state to prevent input focus loss) ══════════ */}
      {modal === "form" && (
        <InvoiceForm
          initial={form}
          contracts={contracts}
          invoices={invoices}
          computeAllPeriods={computeAllPeriods}
          role={role}
          onSubmit={async (payload) => {
            try {
              if (editId) await api.put(`invoices/${editId}/`, payload);
              else await api.post('invoices/', payload);
              close(); load();
            } catch (err) { errMsg(err, 'Save failed'); }
          }}
          onClose={close}
        />
      )}

      {/* ══ PAYMENT RECEIPT MODAL ════════════════════════════════════════ */}
      {modal === "receipt" && receiptInv && (
        <Modal title="RECORD PAYMENT RECEIPT" titleColor="cyan" onClose={close} wide>
          <form onSubmit={saveReceipt} style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: "1rem" }}>

            {/* Invoice summary banner */}
            <div style={{
              background: "rgba(6,182,212,.06)", border: "1px solid rgba(6,182,212,.2)",
              borderRadius: ".75rem", padding: ".85rem 1rem",
              display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: ".4rem .75rem",
              fontSize: ".78rem"
            }}>
              <div style={{ color: "#64748b" }}>Invoice No</div>
              <div style={{ color: "#34d399", fontWeight: 700 }}>
                {receiptInv.invoice_number || `INV-${receiptInv.id}`}
              </div>
              <div style={{ color: "#64748b" }}>Client</div>
              <div style={{ color: "#f1f5f9" }}>{receiptInv.amc_customer_name}</div>
              <div style={{ color: "#64748b" }}>Period</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '.2rem' }}>
                <span style={{ color: "#94a3b8" }}>{receiptInv.period_start} → {receiptInv.period_end}</span>
                {receiptInv.period_label ? (
                  <span style={{ fontSize: '.72rem', color: '#94a3b8' }}>{receiptInv.period_label}</span>
                ) : null}
              </div>
              <div style={{ color: "#64748b", borderTop: "1px solid #1e293b", paddingTop: ".4rem" }}>Invoice Amount</div>
              <div style={{ color: "#34d399", fontWeight: 700, borderTop: "1px solid #1e293b", paddingTop: ".4rem" }}>
                {fmt(receiptInv.grand_total)}
              </div>
            </div>

            {/* Receipt date */}
            <div className="field">
              <label>Date of Receipt</label>
              <DateInput value={receipt.receipt_date} onChange={v => setReceipt({ ...receipt, receipt_date: v })} placeholder="DD/MM/YYYY" />
            </div>

            {/* Actual received + TDS fields */}
            <div style={{
              background: "#020617", border: "1px solid #1e293b",
              borderRadius: ".75rem", padding: "1rem",
              display: "flex", flexDirection: "column", gap: ".75rem"
            }}>
              <div style={{ fontSize: ".65rem", fontWeight: 700, textTransform: "uppercase",
                letterSpacing: ".1em", color: "#22d3ee", marginBottom: ".25rem" }}>
                Payment Breakdown
              </div>

              <div className="field">
                <label>Actual Amount Received (₹)</label>
                <input type="number" step="0.01" value={receipt.amount_received}
                  onChange={e => setReceipt({ ...receipt, amount_received: e.target.value })}
                  placeholder="Cash / NEFT / Cheque amount" />
              </div>

              <div className="grid-2">
                <div className="field">
                  <label>TDS IT (₹)</label>
                  <input type="number" step="0.01" value={receipt.tds_it}
                    onChange={e => setReceipt({ ...receipt, tds_it: e.target.value })}
                    placeholder="Income Tax deducted" />
                </div>
                <div className="field">
                  <label>TDS GST (₹)</label>
                  <input type="number" step="0.01" value={receipt.tds_gst}
                    onChange={e => setReceipt({ ...receipt, tds_gst: e.target.value })}
                    placeholder="GST deducted at source" />
                </div>
              </div>

              {/* Live receipt summary */}
              <div style={{ borderTop: "1px solid #0f172a", paddingTop: ".65rem",
                display: "flex", flexDirection: "column", gap: ".35rem" }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: ".75rem" }}>
                  <span style={{ color: "#64748b" }}>Amount Received</span>
                  <span style={{ color: "#22d3ee", fontFamily: "monospace", fontWeight: 600 }}>{fmt(rAmt)}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: ".75rem" }}>
                  <span style={{ color: "#64748b" }}>TDS IT</span>
                  <span style={{ color: "#fbbf24", fontFamily: "monospace", fontWeight: 600 }}>{fmt(rTdsIT)}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: ".75rem" }}>
                  <span style={{ color: "#64748b" }}>TDS GST</span>
                  <span style={{ color: "#fbbf24", fontFamily: "monospace", fontWeight: 600 }}>{fmt(rTdsGST)}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: ".8rem",
                  borderTop: "1px solid #0f172a", paddingTop: ".4rem" }}>
                  <span style={{ color: "#e2e8f0", fontWeight: 600 }}>Net Settlement</span>
                  <span style={{ color: "#34d399", fontFamily: "monospace", fontWeight: 700 }}>{fmt(rNet)}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: ".8rem" }}>
                  <span style={{ color: "#e2e8f0", fontWeight: 600 }}>Balance Pending</span>
                  <span style={{
                    fontFamily: "monospace", fontWeight: 700,
                    color: rPending > 0 ? "#f87171" : "#34d399"
                  }}>{fmt(rPending)}</span>
                </div>
              </div>
            </div>

            <div style={{ gridColumn: '1 / -1', display: 'flex', gap: '.5rem', justifyContent: 'flex-end' }}>
              <button type="button" className="btn btn-slate" onClick={close}>Cancel</button>
              <button type="submit" className="btn btn-cyan" style={{ justifyContent: "center", padding: ".7rem" }}>Save Receipt</button>
            </div>
          </form>
        </Modal>
      )}

      {/* ══ VIEW MODAL ═══════════════════════════════════════════════════ */}
      {modal === "view" && viewData && (
        <Modal title="INVOICE AUDIT RECORD" titleColor="amber" onClose={close}
          footer={
            <div style={{ display: "flex", gap: ".5rem" }}>
              {viewData.is_deleted && isAdmin && <button className="btn btn-green btn-sm" onClick={() => { restore(viewData.id); close(); }}>Restore</button>}
              <button className="btn btn-slate btn-sm" onClick={close}>Dismiss</button>
            </div>
          }>
          {viewData.is_deleted && <div style={{ padding: ".5rem", background: "#7c2d12", borderRadius: ".25rem", color: "#fed7aa", fontSize: ".875rem", marginBottom: ".75rem" }}>
            ⚠️ This record was deleted on {new Date(viewData.deleted_at).toLocaleString()}
          </div>}
          <div className="detail-grid" style={{ fontSize: ".875rem", display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '.6rem' }}>
            <div className="detail-row">
              <span className="dk emerald">Invoice No:</span>
              <span style={{ color: "#cbd5e1" }}>{viewData.invoice_number || `INV-${viewData.id}`}</span>
            </div>
            <div className="detail-row">
              <span className="dk emerald">Invoice Date:</span>
              <span style={{ color: "#cbd5e1" }}>{viewData.invoice_date || "—"}</span>
            </div>
            <div className="detail-row">
              <span className="dk emerald">Client:</span>
              <span style={{ color: "#cbd5e1" }}>{viewData.amc_customer_name}</span>
            </div>
            <div className="detail-row">
              <span className="dk emerald">AMC:</span>
              <span style={{ color: "#cbd5e1" }}>{viewData.amc_id_display}</span>
            </div>
            <div className="detail-row">
              <span className="dk emerald">Period:</span>
              <span style={{ display: 'flex', flexDirection: 'column', gap: '.25rem', color: '#cbd5e1' }}>
                <span>{viewData.period_start} → {viewData.period_end}</span>
                {viewData.period_label ? (
                  <span style={{ fontSize: '.8rem', color: '#94a3b8' }}>{viewData.period_label}</span>
                ) : null}
              </span>
            </div>
          </div>
          <hr className="detail-divider"/>
          <div className="detail-grid" style={{ fontSize: ".875rem", display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '.6rem' }}>
            <div className="detail-row">
              <span style={{ color: "#94a3b8" }}>Base Amount:</span>
              <span style={{ color: "#cbd5e1" }}>{fmt(viewData.actual_amount)}</span>
            </div>
            <div className="detail-row">
              <span style={{ color: "#94a3b8" }}>Tax ({viewData.tax_percentage}%):</span>
              <span style={{ color: "#fbbf24" }}>{fmt(viewData.tax_value)}</span>
            </div>
            <div className="detail-row" style={{ fontSize: "1rem" }}>
              <span style={{ color: "#e2e8f0", fontWeight: 600 }}>Grand Total:</span>
              <span style={{ color: "#34d399", fontWeight: 700 }}>{fmt(viewData.grand_total)}</span>
            </div>
          </div>
          <hr className="detail-divider"/>
          <div className="detail-grid" style={{ fontSize: ".875rem", display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '.6rem' }}>
            <div className="detail-row">
              <span style={{ color: "#94a3b8" }}>Amt Received:</span>
              <span style={{ color: "#22d3ee" }}>
                {viewData.amount_received ? fmt(viewData.amount_received) : "—"}
              </span>
            </div>
            <div className="detail-row">
              <span style={{ color: "#94a3b8" }}>TDS IT:</span>
              <span style={{ color: "#fbbf24" }}>
                {viewData.tds_it ? fmt(viewData.tds_it) : "—"}
              </span>
            </div>
            <div className="detail-row">
              <span style={{ color: "#94a3b8" }}>TDS GST:</span>
              <span style={{ color: "#fbbf24" }}>
                {viewData.tds_gst ? fmt(viewData.tds_gst) : "—"}
              </span>
            </div>
            <div className="detail-row">
              <span style={{ color: "#94a3b8" }}>Net Settlement:</span>
              <span style={{ color: "#34d399", fontWeight: 700 }}>
                {fmt(viewData.net_received_total)}
              </span>
            </div>
            <div className="detail-row">
              <span style={{ color: "#94a3b8" }}>Receipt Date:</span>
              <span style={{ color: "#cbd5e1" }}>{viewData.receipt_date || "—"}</span>
            </div>
            <div className="detail-row">
              <span style={{ color: "#94a3b8" }}>Pending:</span>
              <span style={{
                color: parseFloat(viewData.pending_amount) > 0 ? "#f87171" : "#34d399",
                fontWeight: 700
              }}>{fmt(viewData.pending_amount)}</span>
            </div>
            <div className="detail-row">
              <span style={{ color: "#94a3b8" }}>Status:</span>
              <span className={`badge ${statusBadge(viewData.status)}`}>{viewData.status}</span>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
